import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  FiChevronDown, FiDownload, FiInfo, FiPercent, FiPlus, FiSave, FiTrash2, FiX,
} from "react-icons/fi";
import {
  Section, Widget, WidgetGrid, tokens,
} from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { Notice, StorageBar } from "../../creditos/styles";
import { Capsule, RoundButton, TintedCapsule } from "../../creditos/components/ControlBar";
import { hairline, surface, tokens as T } from "../../../styles/designTokens";
import useFormacaoPreco from "../hooks/useFormacaoPreco";

/**
 * Cadastro Mestre da formação de preço: contas de despesa, segmentos de receita e os
 * percentuais de cada conta por segmento. As regras e chamadas são as do cadastro
 * anterior; mudam só a apresentação e o uso de interruptores.
 */

const TIPO_REGIME_OPTIONS = [
  { value: "normal", label: "Normal (ICMS/PIS-COFINS c/ crédito)" },
  { value: "servico", label: "Serviço (ISS → CBS/IBS s/ mão de obra)" },
  { value: "ST-monofasico", label: "Substituição Tributária monofásica" },
  { value: "base-reduzida", label: "Base reduzida / crédito presumido" },
];
const CREDITO_OPTIONS = [
  { value: "Sim", label: "Sim" },
  { value: "Nao", label: "Não" },
  { value: "Parcial", label: "Parcial" },
  { value: "A-confirmar", label: "A confirmar" },
];
const STATUS_OPTIONS = [
  { value: "confirmado", label: "Confirmado" },
  { value: "provisorio", label: "Provisório" },
];

const label2 = ({ theme }) => tokens.label2(theme);
const field = ({ theme }) => surface(theme, 2);
const dot = (color) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, opacity: 1 }} />;

const ACCOUNT_COLUMNS = "minmax(180px, 1.6fr) 118px 118px 66px 118px 34px";
const SEGMENT_COLUMNS = "minmax(180px, 1.4fr) minmax(220px, 1.4fr) 92px 66px 72px";
const MATRIX_COLUMNS = "minmax(180px, 1.6fr) 74px 110px 110px 110px";

const TableScroll = styled.div`
  margin-top: 14px;
  overflow-x: auto;
  scrollbar-width: thin;
`;

const Head = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols};
  gap: 12px;
  min-width: ${({ $min }) => $min || 680}px;
  padding-bottom: 7px;
  font-size: 0.7em;
  color: ${({ theme }) => tokens.label3(theme)};
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
`;

const Line = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols};
  gap: 12px;
  align-items: center;
  min-width: ${({ $min }) => $min || 680}px;
  min-height: 50px;
  padding: 6px 0;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  font-size: 0.86em;
  &.on { background: ${({ theme }) => surface(theme, 1)}; }
  .calc { font-weight: 600; letter-spacing: -0.02em; text-align: right; }
`;

// Campo discreto: parece texto em repouso e vira campo ao apontar ou focar
const Inline = styled.input`
  width: 100%;
  min-width: 0;
  height: 32px;
  padding: 0 8px;
  margin-left: -8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.text};
  font: inherit;
  font-weight: 500;
  letter-spacing: -0.012em;
  transition: background 140ms ease, box-shadow 140ms ease;
  &:hover { background: ${field}; }
  &:focus { outline: none; background: ${field}; box-shadow: 0 0 0 2px ${T.brandRing}; }
`;

const Numeric = styled(Inline)`
  margin-left: 0;
  text-align: right;
  background: ${field};
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
`;

const SelectBox = styled.label`
  position: relative;
  display: block;
  select {
    width: 100%;
    height: 32px;
    padding: 0 24px 0 9px;
    border: none;
    border-radius: 8px;
    appearance: none;
    -webkit-appearance: none;
    background: ${field};
    color: ${({ theme }) => theme.text};
    font: inherit;
    font-size: 0.94em;
    text-overflow: ellipsis;
    cursor: pointer;
    color-scheme: ${({ theme }) => (tokens.dk(theme) ? "dark" : "light")};
  }
  select:focus-visible { outline: none; box-shadow: 0 0 0 2px ${T.brandRing}; }
  option { background: ${({ theme }) => tokens.solid(theme)}; color: ${({ theme }) => theme.text}; }
  svg { position: absolute; right: 7px; top: 9px; font-size: 14px; opacity: 0.5; pointer-events: none; }
`;

// Interruptor do iOS
const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 24px;
  cursor: pointer;
  input { position: absolute; inset: 0; margin: 0; opacity: 0; cursor: pointer; z-index: 1; }
  span { position: absolute; inset: 0; border-radius: 999px; background: ${({ $on, theme }) => ($on ? T.brand : tokens.dk(theme) ? "rgba(120,120,128,0.32)" : "rgba(120,120,128,0.16)")}; transition: background 200ms ease; }
  span::after { content: ""; position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; border-radius: 50%; background: #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.2); transform: translateX(${({ $on }) => ($on ? "16px" : "0")}); transition: transform 200ms cubic-bezier(0.3, 1.4, 0.5, 1); }
  input:focus-visible + span { box-shadow: 0 0 0 3px ${T.brandRing}; }
  @media (prefers-reduced-motion: reduce) { span, span::after { transition: none; } }
`;

const AddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
  input {
    flex: 1;
    min-width: 200px;
    height: 34px;
    padding: 0 14px;
    border: none;
    border-radius: 999px;
    background: ${field};
    color: ${({ theme }) => theme.text};
    font: inherit;
    font-size: 0.86em;
  }
  input::placeholder { color: ${label2}; }
  input:focus { outline: none; box-shadow: 0 0 0 3px ${T.brandRing}; }
`;

const Empty = styled.div`
  padding: 22px 0;
  text-align: center;
  font-size: 0.82em;
  color: ${label2};
`;

function Select({ value, options, onChange, label }) {
  return (
    <SelectBox>
      <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <FiChevronDown />
    </SelectBox>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <Switch $on={checked}>
      <input type="checkbox" checked={checked} aria-label={label} onChange={(e) => onChange(e.target.checked)} />
      <span />
    </Switch>
  );
}

function PercentMatrix({ segmento, contas, fp, onClose }) {
  const [linhas, setLinhas] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const existentes = await fp.getPercentuais(segmento.id);
        const map = {};
        existentes.forEach((p) => {
          map[p.contaDespesaId] = { pctHistorico: p.pctHistorico * 100, pctAjusteGerencial: p.pctAjusteGerencial * 100 };
        });
        if (!cancelled) setLinhas(map);
      } catch (e) {
        console.error(e);
        toast.error("Erro ao carregar percentuais.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmento.id]);

  const setLinha = (contaId, key, value) => {
    setLinhas((prev) => ({
      ...prev,
      [contaId]: { ...(prev[contaId] || { pctHistorico: 0, pctAjusteGerencial: 0 }), [key]: value },
    }));
  };

  // Regra do backend: o projetado é sempre calculado, nunca editado
  const projected = (contaId) => {
    const l = linhas[contaId];
    if (!l) return 0;
    return (Number(l.pctHistorico) || 0) * (1 + (Number(l.pctAjusteGerencial) || 0) / 100);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = contas.map((c) => {
        const l = linhas[c.id] || { pctHistorico: 0, pctAjusteGerencial: 0 };
        return {
          contaDespesaId: c.id,
          pctHistorico: (Number(l.pctHistorico) || 0) / 100,
          pctAjusteGerencial: (Number(l.pctAjusteGerencial) || 0) / 100,
          origem: "manual",
        };
      });
      await fp.savePercentuais(segmento.id, payload);
      toast.success(`Percentuais salvos para "${segmento.nome}".`);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao salvar percentuais.");
    } finally {
      setSaving(false);
    }
  };

  const eligible = contas.filter((c) => c.elegivelCredito).reduce((acc, c) => acc + projected(c.id), 0);
  const notEligible = contas.filter((c) => !c.elegivelCredito).reduce((acc, c) => acc + projected(c.id), 0);
  const fmt = (v) => `${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  const total = eligible + notEligible;

  return (
    <Widget $delay={0}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Percentuais: {segmento.nome}</div>
          <div className="w-sub">Histórico e ajuste gerencial por conta. O projetado é calculado</div>
        </div>
        <RoundButton type="button" onClick={onClose} title="Fechar" aria-label="Fechar percentuais" style={{ width: 30, height: 30 }}><FiX /></RoundButton>
      </div>
      {loading && <Empty>Carregando…</Empty>}
      {!loading && contas.length === 0 && <Empty>Cadastre contas de despesa antes de definir os percentuais.</Empty>}
      {!loading && contas.length > 0 && (
        <>
          <TableScroll>
            <Head $cols={MATRIX_COLUMNS} $min={640}>
              <span>Conta de despesa</span>
              <span>Elegível</span>
              <span style={{ textAlign: "right" }}>% Histórico</span>
              <span style={{ textAlign: "right" }}>% Ajuste</span>
              <span style={{ textAlign: "right" }}>% Projetado</span>
            </Head>
            {contas.map((conta) => {
              const l = linhas[conta.id] || { pctHistorico: 0, pctAjusteGerencial: 0 };
              return (
                <Line key={conta.id} $cols={MATRIX_COLUMNS} $min={640}>
                  <span style={{ fontWeight: 500 }}>{conta.nome}</span>
                  <span style={{ color: conta.elegivelCredito ? IOS.green : undefined, opacity: conta.elegivelCredito ? 1 : 0.5 }}>{conta.elegivelCredito ? "Sim" : "Não"}</span>
                  <Numeric type="number" step="0.1" aria-label={`Histórico de ${conta.nome}`} value={l.pctHistorico} onChange={(e) => setLinha(conta.id, "pctHistorico", e.target.value === "" ? "" : Number(e.target.value))} />
                  <Numeric type="number" step="0.1" aria-label={`Ajuste de ${conta.nome}`} value={l.pctAjusteGerencial} onChange={(e) => setLinha(conta.id, "pctAjusteGerencial", e.target.value === "" ? "" : Number(e.target.value))} />
                  <span className="calc">{fmt(projected(conta.id))}</span>
                </Line>
              );
            })}
          </TableScroll>
          <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240, fontSize: "0.8em" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <span><b style={{ fontWeight: 600 }}>{fmt(eligible)}</b> elegível a crédito</span>
                <span><b style={{ fontWeight: 600 }}>{fmt(notEligible)}</b> não elegível</span>
              </div>
              <StorageBar style={{ marginTop: 7 }}>
                <span style={{ flexGrow: Math.max(eligible, 0.01), background: IOS.green }} />
                <span className="rest" style={{ flexGrow: Math.max(notEligible, total > 0 ? 0.01 : 1) }} />
              </StorageBar>
            </div>
            <TintedCapsule type="button" onClick={save} disabled={saving}><FiSave /> {saving ? "Salvando…" : "Salvar percentuais"}</TintedCapsule>
          </div>
        </>
      )}
    </Widget>
  );
}

export default function CadastroPanel() {
  const fp = useFormacaoPreco();
  const [novaConta, setNovaConta] = useState("");
  const [novoSegmento, setNovoSegmento] = useState("");
  const [savingConta, setSavingConta] = useState(false);
  const [segmentoExpandido, setSegmentoExpandido] = useState(null);
  const [savingSegmento, setSavingSegmento] = useState(false);
  const [importingEcd, setImportingEcd] = useState(false);

  if (!fp.projectId) {
    return (
      <Section>
        <Notice style={{ marginTop: 0 }}><FiInfo /><span>Selecione um projeto para configurar o Cadastro Mestre de Formação de Preço.</span></Notice>
      </Section>
    );
  }

  // Ata reunião: "importar a última ECD para o aplicativo, permitindo que o
  // usuário edite, adicione ou exclua contas de despesa diretamente".
  const handleImportarEcd = async () => {
    if (!window.confirm("Importar as contas de despesa da última ECD processada? Contas já existentes (mesmo código ou nome) serão ignoradas.")) return;
    setImportingEcd(true);
    try {
      const resp = await fp.importarEcd();
      if (resp.imported > 0) {
        toast.success(`${resp.imported} conta(s) importada(s) da ECD${resp.skipped > 0 ? `, ${resp.skipped} já existiam` : ""}.`);
        await fp.reload();
      } else {
        toast.info(resp.message || `Nenhuma conta nova importada: ${resp.skipped} já existiam no cadastro.`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Erro ao importar contas da ECD.");
    } finally {
      setImportingEcd(false);
    }
  };

  const handleAddConta = async () => {
    if (!novaConta.trim()) return;
    setSavingConta(true);
    try {
      await fp.createConta({ nome: novaConta.trim(), elegivelCredito: false, statusDado: "provisorio" });
      setNovaConta("");
      toast.success("Conta de despesa cadastrada.");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao cadastrar conta de despesa.");
    } finally {
      setSavingConta(false);
    }
  };

  const handleUpdateConta = async (conta, patch) => {
    try {
      await fp.updateConta(conta.id, patch);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao salvar conta de despesa.");
    }
  };

  const handleDeleteConta = async (conta) => {
    if (!window.confirm(`Remover a conta "${conta.nome}"? Os percentuais cadastrados para ela também serão removidos.`)) return;
    try {
      await fp.deleteConta(conta.id);
      toast.success("Conta removida.");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao remover conta.");
    }
  };

  const handleAddSegmento = async () => {
    if (!novoSegmento.trim()) return;
    setSavingSegmento(true);
    try {
      await fp.createSegmento({ nome: novoSegmento.trim(), tipoRegime: "normal", margemAlvoPadrao: 0.1, ativo: true, ordem: fp.segmentos.length });
      setNovoSegmento("");
      toast.success("Segmento cadastrado.");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao cadastrar segmento.");
    } finally {
      setSavingSegmento(false);
    }
  };

  const handleUpdateSegmento = async (segmento, patch) => {
    try {
      await fp.updateSegmento(segmento.id, patch);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao salvar segmento.");
    }
  };

  const handleDeleteSegmento = async (segmento) => {
    if (!window.confirm(`Remover o segmento "${segmento.nome}"? Os percentuais cadastrados para ele também serão removidos.`)) return;
    try {
      await fp.deleteSegmento(segmento.id);
      if (segmentoExpandido === segmento.id) setSegmentoExpandido(null);
      toast.success("Segmento removido.");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao remover segmento.");
    }
  };

  const eligibleCount = fp.contas.filter((c) => c.elegivelCredito).length;
  const provisionalCount = fp.contas.filter((c) => (c.statusDado || "provisorio") === "provisorio").length;
  const activeCount = fp.segmentos.filter((s) => s.ativo).length;
  const openSegment = fp.segmentos.find((s) => s.id === segmentoExpandido);

  return (
    <>
      <Section>
        <WidgetGrid>
          <Widget $delay={0}>
            <div className="w-head">{dot(IOS.blue)} Contas de despesa</div>
            <div className="w-value">{fp.contas.length}</div>
            <div className="w-foot">{fp.hasEcd ? "ECD disponível para importar" : "Nenhuma ECD processada"}</div>
          </Widget>
          <Widget $delay={50}>
            <div className="w-head">{dot(IOS.green)} Elegíveis a crédito</div>
            <div className="w-value">{eligibleCount}</div>
            <div className="w-foot">{provisionalCount} com dado provisório</div>
          </Widget>
          <Widget $delay={100}>
            <div className="w-head">{dot(IOS.purple)} Segmentos ativos</div>
            <div className="w-value">{activeCount}</div>
            <div className="w-foot">de {fp.segmentos.length} cadastrados</div>
          </Widget>
          <Widget $delay={150}>
            <div className="w-head">{dot(fp.hasCadastroAvancado ? IOS.green : IOS.gray)} Modo da formação de preço</div>
            <div className="w-value" style={{ fontSize: "1.3em", letterSpacing: "-0.02em" }}>{fp.hasCadastroAvancado ? "Avançado" : "Simplificado"}</div>
            <div className="w-foot">{fp.hasCadastroAvancado ? "Usa os segmentos deste cadastro" : "Usa 3 segmentos fixos"}</div>
          </Widget>
        </WidgetGrid>
        <Notice>
          <FiInfo />
          <span>Cadastre as contas de despesa e os segmentos de receita do cliente. Ao cadastrar o primeiro segmento, a Formação de preço deixa os 3 segmentos fixos e passa a usar este cadastro.</span>
        </Notice>
      </Section>

      <Section $delay={80}>
        <Widget $delay={150}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div className="w-title">Contas de despesa</div>
              <div className="w-sub">O direito a crédito de cada conta, hoje e no regime futuro</div>
            </div>
            <Capsule type="button" onClick={handleImportarEcd} disabled={importingEcd} title="Importa o plano de contas (I050) e a DRE (J100) do último arquivo ECD processado">
              <FiDownload /> {importingEcd ? "Importando…" : "Importar última ECD"}
            </Capsule>
          </div>
          <TableScroll>
            <Head $cols={ACCOUNT_COLUMNS} $min={760}>
              <span>Nome da conta</span>
              <span>Crédito PIS/COFINS hoje</span>
              <span>Crédito CBS/IBS futuro</span>
              <span>Elegível</span>
              <span>Status</span>
              <span />
            </Head>
            {fp.contas.map((conta) => (
              <Line key={conta.id} $cols={ACCOUNT_COLUMNS} $min={760}>
                <Inline aria-label="Nome da conta" defaultValue={conta.nome} onBlur={(e) => e.target.value !== conta.nome && handleUpdateConta(conta, { nome: e.target.value })} />
                <Select label="Crédito PIS/COFINS hoje" value={conta.creditoPisCofinsHoje || "A-confirmar"} options={CREDITO_OPTIONS} onChange={(v) => handleUpdateConta(conta, { creditoPisCofinsHoje: v })} />
                <Select label="Crédito CBS/IBS futuro" value={conta.creditoCbsIbsFuturo || "A-confirmar"} options={CREDITO_OPTIONS} onChange={(v) => handleUpdateConta(conta, { creditoCbsIbsFuturo: v })} />
                <Toggle label={`${conta.nome} elegível a crédito`} checked={!!conta.elegivelCredito} onChange={(v) => handleUpdateConta(conta, { elegivelCredito: v })} />
                <Select label="Status do dado" value={conta.statusDado || "provisorio"} options={STATUS_OPTIONS} onChange={(v) => handleUpdateConta(conta, { statusDado: v })} />
                <RoundButton type="button" onClick={() => handleDeleteConta(conta)} title="Remover conta" aria-label={`Remover a conta ${conta.nome}`} style={{ width: 30, height: 30 }}><FiTrash2 /></RoundButton>
              </Line>
            ))}
            {fp.contas.length === 0 && !fp.loading && <Empty>Nenhuma conta de despesa cadastrada ainda.</Empty>}
          </TableScroll>
          <AddRow>
            <input aria-label="Nome da nova conta" placeholder="Nome da nova conta (ex: Salários - Serviços)" value={novaConta} onChange={(e) => setNovaConta(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddConta()} />
            <TintedCapsule type="button" onClick={handleAddConta} disabled={savingConta || !novaConta.trim()}><FiPlus /> Adicionar conta</TintedCapsule>
          </AddRow>
        </Widget>
      </Section>

      <Section $delay={120}>
        <Widget $delay={200}>
          <div className="w-title">Segmentos de receita</div>
          <div className="w-sub">Regime, margem-alvo e percentuais de cada segmento</div>
          <TableScroll>
            <Head $cols={SEGMENT_COLUMNS} $min={720}>
              <span>Nome do segmento</span>
              <span>Tipo de regime</span>
              <span style={{ textAlign: "right" }}>Margem-alvo (%)</span>
              <span>Ativo</span>
              <span />
            </Head>
            {fp.segmentos.map((segmento) => (
              <Line key={segmento.id} $cols={SEGMENT_COLUMNS} $min={720} className={segmentoExpandido === segmento.id ? "on" : ""}>
                <Inline aria-label="Nome do segmento" defaultValue={segmento.nome} onBlur={(e) => e.target.value !== segmento.nome && handleUpdateSegmento(segmento, { nome: e.target.value })} />
                <Select label="Tipo de regime" value={segmento.tipoRegime} options={TIPO_REGIME_OPTIONS} onChange={(v) => handleUpdateSegmento(segmento, { tipoRegime: v })} />
                <Numeric type="number" step="0.1" aria-label={`Margem-alvo de ${segmento.nome}`} defaultValue={(segmento.margemAlvoPadrao * 100).toFixed(1)} onBlur={(e) => handleUpdateSegmento(segmento, { margemAlvoPadrao: Number(e.target.value) / 100 })} />
                <Toggle label={`${segmento.nome} ativo`} checked={!!segmento.ativo} onChange={(v) => handleUpdateSegmento(segmento, { ativo: v })} />
                <span style={{ display: "flex", gap: 6 }}>
                  <RoundButton type="button" $active={segmentoExpandido === segmento.id} onClick={() => setSegmentoExpandido(segmentoExpandido === segmento.id ? null : segmento.id)} title="Configurar percentuais (conta x segmento)" aria-label={`Percentuais de ${segmento.nome}`} style={{ width: 30, height: 30 }}><FiPercent /></RoundButton>
                  <RoundButton type="button" onClick={() => handleDeleteSegmento(segmento)} title="Remover segmento" aria-label={`Remover o segmento ${segmento.nome}`} style={{ width: 30, height: 30 }}><FiTrash2 /></RoundButton>
                </span>
              </Line>
            ))}
            {fp.segmentos.length === 0 && !fp.loading && <Empty>Nenhum segmento cadastrado ainda. A Formação de preço usa o modo simplificado.</Empty>}
          </TableScroll>
          <AddRow>
            <input aria-label="Nome do novo segmento" placeholder="Nome do novo segmento (ex: Revenda de produtos)" value={novoSegmento} onChange={(e) => setNovoSegmento(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddSegmento()} />
            <TintedCapsule type="button" onClick={handleAddSegmento} disabled={savingSegmento || !novoSegmento.trim()}><FiPlus /> Adicionar segmento</TintedCapsule>
          </AddRow>
        </Widget>
      </Section>

      {openSegment && (
        <Section>
          <PercentMatrix segmento={openSegment} contas={fp.contas} fp={fp} onClose={() => setSegmentoExpandido(null)} />
        </Section>
      )}
    </>
  );
}
